import { AhuState, BaseProperty, Entity, Group } from '@app/shared/models/common-models'

export interface Ahu {
  Id: string
  ProjectId: string
  IsMinMaxEnabled: boolean
  UnitCode: string
  UnitURL: string
  UnitImageURL: string
  EcodesignLabelImageURL: string
  EuroventImageUrl: string
  UnitConnectionURL: string
  Entity: Entity
  Errors: boolean
  Category: string
  Categories: Category[]
  Messages: Message[]
  IsHeatPumpAhu: boolean
  RenderImage?: RenderImage
  SystemDrawingImage?: string
  EnergyCoverageImage?: string
  VentilationDurationDiagramImage?: string
  HeatingDurationDiagramImage?: string
  Results?: AhuResults
  AirFlowRangeDiagramData: AirFlowRangeDiagramData
  CapacityDiagramData: CapacityDiagramData[]
  TranslationOverrides: Record<string, string>

  /**
   * Used by UI as an internal storage flag: the flag shows that the AHU is in cache and can be displayed, but it should also
   * be reloaded when opened on ahu page / results page.
   */
  ShouldRefresh: boolean
}

export interface CapacityChangeEvent {
  AirSide: CapacitySide
  Qv: number
  Dp: number
  AirFlowMeasurementUnitKey: string
  PressureDropMeasurementUnitKey: string
}

export interface CapacityDiagramData {
  AirSide: CapacitySide
  Curves: CapacityDiagramCurve[]
  Points: CapacityDiagramPoint[]
  WorkingArea: CapacityDiagramCurve
  QvInterval: number
  DpInterval: number
  QvMin: number
  QvMax: number
  DpMin: number
  DpMax: number
  XAxisTitle: string
  YAxisTitle: string
  AirFlowUnitKey: string
  AirFlowUnitDecimals: number
  PressureDropUnitKey: string
  PressureDropUnitDecimals: number
}

export interface CapacityDiagramCurve {
  Color: string
  IsSystemCurve: boolean
  Points: CapacityDiagramPoint[]
}

export interface CapacityDiagramPoint {
  Qv: number
  Dp: number
  Border: boolean
  Style: DiagramPointStyle
  Color: string
}

export interface SizeSelectDiagramClickEvent {
  newUnitModel: string
  propertyUpdateKey: string
}

export interface AirFlowLine {
  AirFlow: number
  Color: string
  Title: string
  IsSummerLine: boolean
}

export interface AirFlowRangeDiagramData {
  ClickPropertyKey: string
  QvTickValues: number[]
  AirFlowRanges: AirFlowRange[]
  AirFlowLines: AirFlowLine[]
  MeasurementUnitTextId: string
}

export interface AirFlowRange {
  SeriesModelSize: string
  SizeLabel: string
  MinAirFlow: number
  MaxAirFlow: number
  Selected: boolean
}

export interface Category {
  IsSelected: boolean
  Category: string
  CategoryTextId: string
  TypologyTextId: string
  ImageUrl: string
}

export interface Message {
  Level: MessageLevel
  MessageTextId: string
  SenderNameTextId: string
}

export interface SystemDrawing {
  ComponentKey: string
  ComponentTitleId: string
}

export interface AhuResults {
  State: AhuState
  Ahu: ProductResultEntity
  SystemDrawingDescriptions: SystemDrawing[]
  Messages: Message[]
  TranslationOverrides: Record<string, string>
}

export interface ProductResultEntity extends Entity {
  Groups: Group[]
  Children: ProductResultEntity[]
  Messages: Message[]
}

export interface RenderImage {
  View: ViewOption | 'unit' | 'connections'
  Source: string
}

export enum MessageLevel {
  Error = 0,
  Warning,
  Info,
}

export enum DiagramPointStyle {
  Circle = 0,
  Diamond = 1,
  Square = 2,
}

export enum CapacitySide {
  Supply = 0,
  Extract = 1,
}

export enum ViewOption {
  FRONT = 1,
  TOP = 2,
  LEFT = 4,
  RIGHT = 8,
  TOPLEFT = 16,
  TOPRIGHT = 32,
}

export interface SFPeTable {
  Rows: SFPeRow[]
  Order: string[]
  Translations: { [key: string]: string }
  MeasurementUnits: { [key: string]: string }
}

export interface SFPeRow {
  FanSpeed: string
  SFPe: number
  En308: number
}

export interface SoundResult {
  Key: string
  TextId: string
  IsVisible: boolean
  Properties: BaseProperty[]
}
export interface DiagramTrackingValue {
  xAxisValue: number
  yAxisValue: number
  xAxisTitle?: string
  yAxisTitle?: string
  airSide?: number
}
